import barba from '@barba/core';
import type { ITransitionData } from '@barba/core';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { getNamespaceFromDom, runPageInit } from './page-init-registry';
import { initTeamMemberCards } from '../widgets/team-member-cards';
import { applySubnavForNamespace } from '../widgets/page-subnav/apply-subnav-for-namespace';
import { destroyPermissionRequest } from '../widgets/permission-request';

const FADE_OUT_MS = 200;
const FADE_IN_MS = 300;

/** Клик по page-tabs (как в residency-become) — не сбрасывать скролл */
let preserveScrollOnEnter = false;
let savedScrollY = 0;

function resetPageScroll(): void {
  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}

function restorePageScroll(y: number): void {
  window.scrollTo(0, y);
  document.documentElement.scrollTop = y;
  document.body.scrollTop = y;
}

function bindPageTabsScrollPreserve(): void {
  document.addEventListener(
    'click',
    (event) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const target = event.target;

      if (!(target instanceof Element)) return;

      const link = target.closest<HTMLAnchorElement>('a.page-tabs__nav-link[href]');

      if (!link) return;

      const href = link.getAttribute('href');

      if (!href || href === '#' || href.startsWith('javascript:')) return;

      savedScrollY = window.scrollY || document.documentElement.scrollTop || 0;
      preserveScrollOnEnter = true;
    },
    true,
  );
}

export function syncBodyClusterZoneFromPage(container?: ParentNode | null): void {
  const root = container ?? document;
  const link = root.querySelector<HTMLElement>('.clusters-list__link--current[data-zone-id]');
  const zone = link?.dataset.zoneId?.trim();
  if (zone) {
    document.body.setAttribute('data-current-zone-id', zone);
  } else {
    document.body.removeAttribute('data-current-zone-id');
  }
}

function fadeOpacity(
  el: HTMLElement,
  durationMs: number,
  from: number,
  to: number,
  easing: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const anim = el.animate([{ opacity: from }, { opacity: to }], {
      duration: durationMs,
      easing,
      fill: 'forwards',
    });
    anim.finished
      .then(() => {
        anim.commitStyles?.();
        anim.cancel();
        resolve();
      })
      .catch(reject);
  });
}

export function initBarba(): void {
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  bindPageTabsScrollPreserve();

  barba.hooks.afterEnter((data: ITransitionData) => {
    initTeamMemberCards();
    const container = data.next.container as HTMLElement | undefined;

    const resolveNamespace = (): string | undefined =>
      getNamespaceFromDom(container) ?? data.next.namespace ?? getNamespaceFromDom();

    const emitBarbaEnter = (namespace: string | undefined): void => {
      syncBodyClusterZoneFromPage(container);
      runPageInit(namespace, container);
      applySubnavForNamespace(namespace);
      ScrollTrigger.refresh();

      if (preserveScrollOnEnter) {
        restorePageScroll(savedScrollY);
        requestAnimationFrame(() => restorePageScroll(savedScrollY));
        preserveScrollOnEnter = false;
      } else {
        resetPageScroll();
        requestAnimationFrame(resetPageScroll);
      }

      window.dispatchEvent(new Event('stc:barba-after-enter'));
    };

    let namespace = resolveNamespace();
    if (namespace) {
      emitBarbaEnter(namespace);
      return;
    }

    queueMicrotask(() => {
      namespace = resolveNamespace();
      if (namespace) {
        emitBarbaEnter(namespace);
        return;
      }
      requestAnimationFrame(() => {
        emitBarbaEnter(resolveNamespace());
      });
    });
  });

  barba.init({
    transitions: [
      {
        name: 'default',
        async leave(data: ITransitionData) {
          destroyPermissionRequest();
          window.dispatchEvent(
            new CustomEvent('stc:barba-before-leave', {
              detail: { nextNamespace: data.next.namespace },
            }),
          );

          if (preserveScrollOnEnter) return;

          const el = data.current.container;
          await fadeOpacity(el, FADE_OUT_MS, 1, 0, 'ease-out');
        },
        async enter(data: ITransitionData) {
          const el = data.next.container;
          if (preserveScrollOnEnter) {
            restorePageScroll(savedScrollY);
            el.style.removeProperty('opacity');
            return;
          }
          resetPageScroll();
          el.style.opacity = '0';
          await fadeOpacity(el, FADE_IN_MS, 0, 1, 'ease-in');
          el.style.removeProperty('opacity');
        },
      },
    ],
  });
}
